/**
 * Kontrakten for systemadmin (#178, epic #167 D3/D8).
 *
 * Systemadminen står utenfor alle familier: egen tabell (`site_admins`, aldri
 * en `members`-rad), egen cookie og egne ruter under `/api/system`. En
 * familieøkt gir aldri tilgang dit, og systemadminøkten gir ingenting på
 * familierutene. Innlogging krever passord OG en TOTP-kode (eller en
 * engangs gjenopprettingskode).
 */
import { z } from 'zod';
import { FamilyCode, PASSWORD_MAX, Username } from './auth';
import { FamilyName, HouseholdLat, HouseholdLon } from './onboarding';

/**
 * Cookien for systemadminøkten. `HttpOnly`, `SameSite=Strict` og
 * `Path=/api/system`, så nettleseren aldri sender den til familierutene.
 */
export const SITE_ADMIN_COOKIE_NAME = 'stell_site_sid' as const;
export const SITE_ADMIN_COOKIE_PATH = '/api/system' as const;

/**
 * Koden er enten 6 sifre fra autentiseringsappen eller en
 * gjenopprettingskode (`xxxx-xxxx-xxxx`). Formen sjekkes ikke her: en feil
 * form skal gi samme `invalid_credentials` som en feil kode.
 */
const SecondFactor = z.string().min(1).max(64);

// ─── POST /api/system/auth/login ────────────────────────────────────────────
// 200 SiteAdminLoginResponse + Set-Cookie | 401 invalid_credentials |
// 401 locked + Retry-After | 429 rate_limited + Retry-After. Bare
// application/json (415 ellers), som /api/auth/login.
export const SiteAdminLoginBody = z.object({
  username: z.string().min(1).max(200),
  password: z.string().min(1).max(PASSWORD_MAX),
  code: SecondFactor,
});
export type SiteAdminLoginInput = z.infer<typeof SiteAdminLoginBody>;

/** Feilkodene fra innloggingen. Svaret sier aldri hvilken faktor som var feil. */
export type SiteAdminLoginError = 'invalid_credentials' | 'locked' | 'rate_limited';

export interface SiteAdminMe {
  username: string;
  /** Til når fersk re-autentisering gjelder, eller null. */
  reauth_expires_at: string | null;
  /** Ubrukte gjenopprettingskoder. Bør ikke gå tomt. */
  recovery_codes_left: number;
}

export interface SiteAdminLoginResponse {
  ok: true;
  admin: SiteAdminMe;
}

// ─── POST /api/system/auth/reauth ───────────────────────────────────────────
// Fersk re-autentisering i 10 min for destruktive handlinger (som
// `requireAdminScope` for familieadmin). 200 { ok, reauth_expires_at } |
// 401 wrong_credentials | 401 code_already_used (koden ble nettopp brukt —
// vent på neste) | 401 locked + Retry-After.
export const SiteAdminReauthBody = z.object({
  password: z.string().min(1).max(PASSWORD_MAX),
  code: SecondFactor,
});
export type SiteAdminReauthInput = z.infer<typeof SiteAdminReauthBody>;

// ─── POST /api/system/auth/recovery-codes (krever re-autentisering) ────────
// Erstatter alle gjenopprettingskodene. Kodene vises bare i dette svaret.
export interface SiteAdminRecoveryCodes {
  codes: string[];
}

// ─── Familiene (FF-11, #179) ────────────────────────────────────────────────
// Systemadminen oppretter, suspenderer og sletter familier, og ser bare tall
// og nøkler om dem — aldri familieinnhold (D3). Alle skrivende kall krever
// fersk re-autentisering (403 `reauth_required` ellers).

export type SystemFamilyStatus = 'pending' | 'active' | 'suspended';

export interface SystemFamily {
  id: string;
  name: string;
  /** Familiekoden (`families.slug`, D5). */
  code: string;
  status: SystemFamilyStatus;
  member_count: number;
  created_at: string;
  /** Siste aktivitet i en levende økt i familien, eller null. */
  last_active_at: string | null;
  suspended_at: string | null;
  /** Familieadminen har ikke fullført oppsettet ennå. */
  setup_pending: boolean;
  smarthome_enabled: boolean;
  feedback_enabled: boolean;
  /** AI-bryteren (FF-13, #181). Av: assistent, briefing-AI og AI-tilbakemelding stopper. */
  ai_enabled: boolean;
  /** Månedlig tokenbudsjett for AI; 0 = ubegrenset (FF-13, #181). */
  ai_budget_tokens_month: number;
  /** Tokens familien har brukt denne Oslo-måneden — et tall, ikke innhold (D3). */
  ai_tokens_month: number;
}

/** Høyeste tokenbudsjett per måned panelet godtar: en milliard, langt over noe reelt. */
export const AI_BUDGET_TOKENS_MAX = 1_000_000_000;

/** En familieadmin: bare det som trengs for å gi en ny lenke. */
export interface SystemFamilyAdmin {
  id: string;
  name: string;
  username: string | null;
  /** Har valgt eget passord. Uten: lenken er en invitasjon, ellers tilbakestilling. */
  has_password: boolean;
}

/** En engangslenke (D7). Vises én gang; bare hashen lagres. */
export interface SystemFamilyLink {
  purpose: 'enroll' | 'password_reset';
  member_id: string;
  url: string;
  expires_at: string;
}

// GET /api/system/families
export interface SystemFamilyList {
  families: SystemFamily[];
}

// POST /api/system/families → 201 SystemFamilyCreated |
// 409 family_code_taken | 400 invalid_body
export const SystemFamilyCreateBody = z.object({
  name: FamilyName,
  code: FamilyCode,
  householdLat: HouseholdLat,
  householdLon: HouseholdLon,
  admin: z.object({
    name: z.string().trim().min(1).max(60),
    username: Username,
  }),
});
export type SystemFamilyCreateInput = z.infer<typeof SystemFamilyCreateBody>;

export interface SystemFamilyCreated {
  family: SystemFamily;
  admin: SystemFamilyAdmin;
  link: SystemFamilyLink;
}

// GET /api/system/families/:id/admins
export interface SystemFamilyAdminList {
  admins: SystemFamilyAdmin[];
}

// POST /api/system/families/:id/admins/:memberId/link → SystemFamilyLink

// PATCH /api/system/families/:id → SystemFamily |
// 409 family_code_taken | 409 smarthome_taken (bare én familie kan ha smarthuset)
export const SystemFamilyUpdateBody = z
  .object({
    code: FamilyCode.optional(),
    smarthome_enabled: z.boolean().optional(),
    feedback_enabled: z.boolean().optional(),
    ai_enabled: z.boolean().optional(),
    ai_budget_tokens_month: z.number().int().min(0).max(AI_BUDGET_TOKENS_MAX).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'empty_update' });
export type SystemFamilyUpdateInput = z.infer<typeof SystemFamilyUpdateBody>;

// POST /api/system/families/:id/suspend | /reactivate → SystemFamily

// DELETE /api/system/families/:id — body { code } → { ok: true } |
// 400 code_mismatch | 409 not_suspended (suspender først)
export const SystemFamilyDeleteBody = z.object({
  code: z.string().min(1).max(64),
});
export type SystemFamilyDeleteInput = z.infer<typeof SystemFamilyDeleteBody>;

// ─── Statistikk (FF-12, #180) ───────────────────────────────────────────────
// GET /api/system/stats?since&until → SystemStats | 400 invalid_query.
// Bare tall (D3): antall, summer og tider per familie og for hele instansen.
// Aldri tekst, titler, varenavn, navn på hendelser eller kosthold, og
// medlemmer telles, de navngis ikke.

/** Det lengste vinduet statistikken regner over, i dager. */
export const SYSTEM_STATS_MAX_DAYS = 366;

/** En kalenderdato `YYYY-MM-DD` som faktisk finnes. */
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (v) => {
      const d = new Date(`${v}T00:00:00Z`);
      return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(v);
    },
    { message: 'invalid_date' },
  );

/** Antall kalenderdager fra og med `since` til og med `until`. */
export function systemStatsDays(since: string, until: string): number {
  return Math.round((Date.parse(`${until}T00:00:00Z`) - Date.parse(`${since}T00:00:00Z`)) / 86_400_000) + 1;
}

/**
 * Vinduet er hele kalenderdager i Europe/Oslo, begge ender med. Uten
 * parametre: de siste 30 dagene til og med i dag.
 */
export const SystemStatsQuery = z
  .object({ since: CalendarDate, until: CalendarDate })
  .partial()
  .refine((v) => (v.since === undefined) === (v.until === undefined), { message: 'since_and_until_together' })
  .refine((v) => !v.since || !v.until || v.since <= v.until, { message: 'since_after_until' })
  .refine((v) => !v.since || !v.until || systemStatsDays(v.since, v.until) <= SYSTEM_STATS_MAX_DAYS, {
    message: 'window_too_long',
  });
export type SystemStatsQueryInput = z.infer<typeof SystemStatsQuery>;

/** AI-bruken på én modell i vinduet. `model` er modell-id-en, ikke innhold. */
export interface SystemStatsAiModel {
  model: string;
  turns: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

/**
 * Tallene for en familie eller for hele instansen. «Nå»-tall (medlemmer,
 * enheter, aktive oppgavemaler) gjelder i dag; resten teller det som skjedde
 * i vinduet.
 */
export interface SystemStatsCounts {
  ai_turns: number;
  ai_input_tokens: number;
  ai_output_tokens: number;
  ai_cost_usd: number;
  ai_by_model: SystemStatsAiModel[];
  /** Nye samtaler med assistenten. */
  conversations: number;
  /** Meldinger fra medlemmene og svar fra assistenten (ikke verktøykall). */
  messages: number;
  /** Aktive oppgavemaler nå. */
  chore_templates: number;
  chores_spawned: number;
  chores_completed: number;
  chores_approved: number;
  events_created: number;
  shopping_added: number;
  shopping_checked: number;
  meals_planned: number;
  points_awarded: number;
  /** Medlemmer som ikke er arkivert, nå. */
  members: number;
  /** Innrullerte enheter som ikke er trukket tilbake, nå. */
  devices: number;
  /**
   * Medlemmer med en økt brukt i vinduet. Utløpte økter slettes, så tallet er
   * en nedre grense for vinduer lenger enn øktenes levetid.
   */
  active_members: number;
}

export interface SystemStatsFamily extends SystemStatsCounts {
  id: string;
  name: string;
  /** Familiekoden (`families.slug`, D5). */
  code: string;
  status: SystemFamilyStatus;
  /** Siste aktivitet i en levende økt i familien, eller null. */
  last_active_at: string | null;
}

export interface SystemStats {
  window: {
    /** Kalenderdatoene (Europe/Oslo), begge med. */
    since: string;
    until: string;
    /** Det samme som UTC-øyeblikk: `from` med, `to` ikke med. */
    from: string;
    to: string;
  };
  totals: SystemStatsCounts & { families: number };
  families: SystemStatsFamily[];
}
